import {
	Grid,
	Skeleton,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableContainer,
	Alert,
	useTheme,
} from '@mui/material';
import React from 'react';
import { CerebroLink, CerebroSecondaryButton } from '../../../shared/components';
import { EmptyGuid } from '../../../shared/constants';
import { TermSheetCondition, TermSheetRevision } from '../../../shared/models';
import { ConditionsDeleteDialog } from './ConditionsDeleteDialog';
import { ConditionsForm } from './ConditionsForm';
import { PlatformRootUrl } from '../../../shared/utils/platform';

interface ConditionsPageProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	revision: TermSheetRevision | null;
	isLoading: boolean;
}

export const ConditionsPage: React.FC<ConditionsPageProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	revision,
	isLoading,
}) => {
	const [conditionBeingEdited, setConditionBeingEdited] = React.useState<TermSheetCondition | null>(null);
	const [conditionBeingDeleted, setConditionBeingDeleted] = React.useState<TermSheetCondition | null>(null);
	const theme = useTheme();
	const adminUrl = `${PlatformRootUrl}/Admin/ConditionTypes`;

	const onAddCondition = () => {
		setConditionBeingEdited({
			id: 0,
			guid: EmptyGuid,
			conditionText: '',
			conditionTypeId: 0,
			type: '',
		} as TermSheetCondition);
	};

	const closeDrawersAndDialogs = () => {
		setConditionBeingEdited(null);
		setConditionBeingDeleted(null);
	};

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<>
			<Grid container>
				<Grid item md={1} xs={12}>
					<CerebroSecondaryButton onClick={() => onAddCondition()} sx={{ marginBottom: 5 }}>
						Add
					</CerebroSecondaryButton>
				</Grid>
				<Grid item md={11} xs={12}>
					<Alert
						severity="info"
						sx={{
							marginBottom: 3,
							boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
							border: 'none',
							backgroundColor: 'white',
							fontWeight: 'bold',
						}}
					>
						Add other conditions for the term sheet here by selecting the condition type and entering
						details. A condition type should only be used once per term sheet. New condition types can be
						added{' '}
						<CerebroLink href={adminUrl} target="_blank" sx={{ cursor: 'pointer' }}>
							here
						</CerebroLink>
						.
					</Alert>
				</Grid>
			</Grid>

			<Grid container spacing={2} alignItems="stretch">
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Type</TableCell>
								<TableCell>Text</TableCell>
								<TableCell>&nbsp;</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{revision?.conditions.map(condition => {
								return (
									<TableRow key={condition.guid}>
										<TableCell>{condition.type}</TableCell>
										<TableCell>{condition.conditionText}</TableCell>
										<TableCell>
											<CerebroSecondaryButton onClick={() => setConditionBeingEdited(condition)}>
												Edit
											</CerebroSecondaryButton>
											<CerebroSecondaryButton onClick={() => setConditionBeingDeleted(condition)}>
												Delete
											</CerebroSecondaryButton>
										</TableCell>
									</TableRow>
								);
							})}
							{(!revision?.conditions || revision?.conditions.length == 0) && (
								<TableRow>
									<TableCell sx={{ textAlign: 'center' }} colSpan={4}>
										No conditions has been added for the term sheet
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>

			<ConditionsForm
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				condition={conditionBeingEdited}
				close={closeDrawersAndDialogs}
				isLoading={isLoading}
			/>

			<ConditionsDeleteDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				condition={conditionBeingDeleted}
				close={closeDrawersAndDialogs}
			/>
		</>
	);
};
